import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import { ButtressEntity } from '@buttress/crag';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import { callLambda } from '../../../../helpers/lambda.js';
import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';
import type WaRadioGroup from '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/subscription/select-card.json';
import { jobSchema } from '../../job-schema.js';

interface PaymentMethod {
  id: string;
  ownerId: string;
  type: string;
  brand: string;
  last4: string;
  active: boolean;
}

enum JobStatus {
  SELECTING_CARD,
  REQUEST_CARD_AUTHORISATION,
  REQUESTED_CARD_AUTHORISATION,
  SELECTED,
}

@customElement(NSSelectSubscriptionCard.is)
export default class NSSelectSubscriptionCard extends NSJobComponent {
  static get is() {
    return 'ns-select-subscription-card';
  }

  @state()
  private __subscription?: ButtressEntity;

  private __selectedPaymentMethod?: string;

  private __authroisationReason?: string;

  @state()
  private __jobStatus: JobStatus = JobStatus.SELECTING_CARD;

  @state()
  private __selectedLast4?: string;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this._job) throw new Error('Unable to find job');

    if (!this._directory) throw new Error('Directory service not found');
    if (!this._directory.me || !this._directory.me.companyId)
      throw new Error('Company id not found in the directory service');

    this.__subscription = await this.loadInput('subscription', 'subscriptionId');

    this.__selectedPaymentMethod = this.__subscription.paymentMethods.find((p: PaymentMethod) => p.active)?.id;

    const jobNeedsAuthMetadata = this._job.metadata.find((m) => m.key === 'NEEDS_AUTHORISATION');
    if (jobNeedsAuthMetadata && jobNeedsAuthMetadata.value === 'TRUE') {
      this.__jobStatus = JobStatus.REQUEST_CARD_AUTHORISATION;
      return;
    }

    const jobRequestedAuthMetadata = this._job.metadata.find((m) => m.key === 'REQUESTED_AUTHORISATION');
    if (jobRequestedAuthMetadata && jobRequestedAuthMetadata.value === 'TRUE') {
      this.__jobStatus = JobStatus.REQUESTED_CARD_AUTHORISATION;
      return;
    }

    const jobSuccessfullySelectedCard = this._job.metadata.find((m) => m.key === 'SUCCESSFULLY_SELECTED_CARD');
    if (jobSuccessfullySelectedCard && jobSuccessfullySelectedCard.value) {
      const selectedCard = this.__subscription.paymentMethods.find(
        (p: PaymentMethod) => p.id === jobSuccessfullySelectedCard.value,
      );
      this.__jobStatus = JobStatus.SELECTED;
      this.__selectedLast4 = selectedCard ? selectedCard.last4 : undefined;
    }
  }

  private async __selectCard() {
    this.clearError();
    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this._directory || !this._directory.me) throw new Error('Directory service not found');
      if (!this._job) throw new Error('Missing job');
      if (!this.__subscription) throw new Error('Can not find subscription');

      const subPaymentMethod = this.__subscription.paymentMethods.find(
        (p: PaymentMethod) => p.id === this.__selectedPaymentMethod,
      );
      if (subPaymentMethod.active)
        throw new Error("Selected payment method is already the card's payment method for the subscription");
      if (!subPaymentMethod) throw new Error('Please select a valid payment method');

      if (this._job.metadata.some((m) => m.key === 'NEEDS_AUTHORISATION') && !this.__authroisationReason)
        throw new Error('Please provide an authorisation reason');

      this.loading('Selecting Subscription Card');
      const output = await callLambda(
        'subscription/select-card',
        'POST',
        this._auth.token,
        JSON.stringify({
          subscriptionId: this.__subscription.id,
          paymentMethodId: this.__selectedPaymentMethod,
          requestReason: this.__authroisationReason,
          jobId: this._job.id,
        }),
      );
      this.loaded();

      const outcome = output.res as unknown as { output: string };
      if (outcome.output === 'SUCCESSFULLY_SELECTED_CARD') {
        const selectedCard = this.__subscription.paymentMethods.find(
          (p: PaymentMethod) => p.id === this.__selectedPaymentMethod,
        );
        this.__selectedLast4 = selectedCard.last4;
        this.__jobStatus = JobStatus.SELECTED;
        return;
      } else if (outcome.output === 'NEEDS_AUTHORISATION') {
        this.__jobStatus = JobStatus.REQUEST_CARD_AUTHORISATION;
      } else if (outcome.output === 'REQUESTED_AUTHORISATION') {
        this.__jobStatus = JobStatus.REQUESTED_CARD_AUTHORISATION;
      } else {
        throw new Error('Lambda returned unexpected output outcome');
      }
    } catch (err: any) {
      this.loaded();
      this.showError(err, 'Something went wrong');
      throw new Error(err.message);
    }
  }

  render() {
    switch (this.__jobStatus) {
      case JobStatus.REQUEST_CARD_AUTHORISATION:
        return html`
          <ns-job-layout>
            <p slot="intro">You selected a card that you do not own within nodeStream.</p>
            <div class="job-panel">
              <p>If you would like to proceed, could you please provide us with additional reasoning or context?</p>
              <wa-textarea
                placeholder="Authorisation Request Reason"
                @input="${(e: CustomEvent) => {
                  const path = e.composedPath();
                  const [eventTarget] = path;
                  const target = eventTarget as HTMLTextAreaElement;
                  this.__authroisationReason = target.value;
                }}"
              ></wa-textarea>
            </div>
            <div slot="aside" class="primary-info">
              <span class="info-icon" aria-hidden="true">i</span>
              <p>nodeStream will send a request to the owner asking to use their card for the subscription.</p>
              <p>Your explanation will help the card owner to understand why such a request is being made.</p>
            </div>
          </ns-job-layout>
        `;
      case JobStatus.REQUESTED_CARD_AUTHORISATION:
        return html`
          <ns-job-layout>
            <div class="job-panel">
              <p>
                You selected a card that you do not own within nodeStream. We have sent a request to the owner asking to
                use it for the subscription.
              </p>
            </div>
          </ns-job-layout>
        `;
      case JobStatus.SELECTED:
        return html`
          <ns-job-layout>
            <div class="job-panel">
              <p>
                You have successfully updated your subscription default payment. Any future payments for this
                subscription will be go out from the card that ends with ${this.__selectedLast4}.
              </p>
            </div>
          </ns-job-layout>
        `;
      default:
        return html`
          <ns-job-layout>
            <p slot="intro">
              Please select one of the cards already saved in your subscription account to set it as the default payment
              method for all future invoices.
            </p>
            <div class="job-panel">
              <wa-radio-group
                .value=${this.__selectedPaymentMethod ?? null}
                @change=${(e: Event) => (this.__selectedPaymentMethod = (e.target as WaRadioGroup).value as string)}
              >
                <span slot="label" class="visually-hidden">Saved cards</span>
                ${this.__subscription?.paymentMethods.map(
                  (p: PaymentMethod) => html`
                    <wa-radio class="job-option" value=${p.id}>Card ends with ${p.last4}</wa-radio>
                  `,
                )}
              </wa-radio-group>
            </div>
          </ns-job-layout>
        `;
    }
  }

  renderActions() {
    switch (this.__jobStatus) {
      case JobStatus.REQUEST_CARD_AUTHORISATION:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.__selectCard()}>Send Request</wa-button>
        `;
      case JobStatus.REQUESTED_CARD_AUTHORISATION:
      case JobStatus.SELECTED:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.outcome('complete')}>Complete</wa-button>
        `;
      default:
        return html`
          <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.outcome('complete')}>
            Cancel
          </wa-button>
          <wa-button variant="brand" type="button" @click=${() => this.__selectCard()}>Select Card</wa-button>
        `;
    }
  }
}
